import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import * as actions from 'actions/external-api';
import { fetchAllUserStatus } from 'api/utils';
import { createEAUser } from 'api/external-api';
import {
  FlexRow,
  PaddingWrapper,
  PlainButton,
} from 'components/styled-components';
import ApiTable from 'containers/Manage-Admin/api-users/table';
import {
  Dialog,
  Button,
  InputGroup,
  FormGroup,
  Intent,
} from '@blueprintjs/core';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import ErrorWrapper from 'components/forms/components/error-wrapper';
import Controls from 'containers/Manage-Admin/api-users/controls';
import Pagination from 'components/pagination';
import { AppToaster } from 'components/AppToaster';

const EmailValidation = Yup.object().shape({
  userEmail: Yup.string()
    .email('Invalid Email')
    .required('Required')
    .max(200, 'Too long!'),
});

const STATUS_OPTIONS = [{ label: 'All', value: null }];

const ApiUsersBase = ({ users, isFetching, fetchEAUsers }) => {
  const [isOpen, toggleOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS[0]);
  const [statusOptions, setStatusOptions] = useState(STATUS_OPTIONS);

  useEffect(() => {
    fetchAllUserStatus().then(({ data }) => {
      const mapped = data.map(status => ({
        label: status.statusName,
        value: status.userStatusID,
      }));
      setStatusOptions([...statusOptions, ...mapped]);
    });
  }, []);
  useEffect(() => {
    fetchUsers();
  }, [currentPage, perPage, searchQuery, statusFilter]);

  const fetchUsers = () => {
    fetchEAUsers({
      start: currentPage * perPage,
      limit: perPage,
      userStatus: statusFilter.value,
      searchQuery,
    });
  };

  const handleClose = () => toggleOpen(false);

  const handlePageClick = page => {
    setCurrentPage(page);
  };

  const handlePerPageChange = page => {
    setPerPage(page.value);
    setCurrentPage(0);
  };

  const handleQueryChange = query => {
    setSearchQuery(query);
    setCurrentPage(0);
  };

  const handleChangeFilter = selected => {
    setStatusFilter(selected);
    setCurrentPage(0);
  };

  const createUser = ({ userEmail }, actions) => {
    if (userEmail && actions) {
      createEAUser(userEmail)
        .then(res => {
          actions.setSubmitting(false);
          AppToaster.show({
            intent: Intent.SUCCESS,
            message: res.data.message,
          });
          handleClose();
          if (currentPage !== 0) {
            setCurrentPage(0);
          } else {
            fetchUsers();
          }
        })
        .catch(err => {
          AppToaster.show({
            intent: Intent.DANGER,
            message: err.response.data.message,
          });
          actions.setSubmitting(false);
        });
    }
  };
  return (
    <>
      <PaddingWrapper padding="0 1rem 1rem">
        {/* open the dialog to create a new api user */}
        <FlexRow>
          <Controls
            statusOptions={statusOptions}
            status={statusFilter}
            handleChangeFilter={handleChangeFilter}
            handleQueryChange={handleQueryChange}
          />
          <PlainButton onClick={toggleOpen}>add api user</PlainButton>
        </FlexRow>

        {/* dialog to consume user information and display the api key */}
        <Dialog
          isOpen={isOpen}
          onClose={handleClose}
          title="Create New API User"
        >
          <Formik
            initialValues={{ userEmail: '' }}
            validationSchema={EmailValidation}
            onSubmit={createUser}
          >
            {({ isSubmitting, handleChange, values, isValid, handleBlur }) => (
              <Form>
                <PaddingWrapper>
                  <FormGroup label="API User Email">
                    <InputGroup
                      name="userEmail"
                      onChange={handleChange}
                      value={values.userEmail}
                      onBlur={handleBlur}
                    />
                  </FormGroup>
                  <ErrorMessage
                    name="userEmail"
                    render={msg => <ErrorWrapper>{msg}</ErrorWrapper>}
                  />
                  <FlexRow justify="flex-end">
                    <Button minimal onClick={handleClose} type="button">
                      Cancel
                    </Button>
                    <PlainButton
                      onClick={createUser}
                      disabled={isSubmitting || !isValid}
                      type="submit"
                    >
                      create user
                    </PlainButton>
                  </FlexRow>
                </PaddingWrapper>
              </Form>
            )}
          </Formik>
        </Dialog>
      </PaddingWrapper>
      <ApiTable
        users={users.result}
        isFetching={isFetching}
        totalCount={users.totalCount}
        fetchUsers={fetchUsers}
        searchQuery={searchQuery}
      />
      <Pagination
        currentPage={currentPage}
        perPage={perPage}
        numRecords={users.totalCount}
        onPageClick={handlePageClick}
        handlePerPageChange={handlePerPageChange}
      />
    </>
  );
};

const mapStateToProps = state => ({
  users: state.externalApi.users,
  isFetching: state.externalApi.isFetching,
});
const ApiUsers = connect(
  mapStateToProps,
  actions
)(ApiUsersBase);

export default ApiUsers;
